import React, { useState } from 'react';
import moment from 'moment';
import { sendApiRequest } from 'lib/api';
import notify from 'lib/notify';
import CompaniesAutoComplete from 'components/common/CompaniesAutocomplete';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Link,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

type Slot = {
  isAvailable: boolean;
  nearestAvailableDate: Date;
};

type Props = {
  slotInfo: Slot;
  companies: any[];
};

const Featured = ({ slotInfo, companies = [] }: Props) => {
  const [showModal, setShowModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);

  const onPromote = () => {
    if (!selectedCompany?._id) {
      notify('Please select a company');
      return;
    }

    setIsLoading(true);

    sendApiRequest({
      path: `/companies/promote`,
      data: {
        companyId: selectedCompany._id,
      },
    })
      .then((response) => {
        window.open(response.paymentUrl);
      })
      .catch((e) => {
        notify(e.message);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const renderModal = () => {
    return (
      <Dialog open={showModal} onClose={() => setShowModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle
          sx={{
            background: '#dce7f1',
            pr: 6,
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <Box component="span" sx={{ flex: '1 1 auto', minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography>Promote a company -</Typography>
              <Link
                href="/promote"
                target="_blank"
                rel="noopener noreferrer"
                sx={{ fontSize: '1rem', textDecoration: 'none' }}
              >
                How it works
              </Link>
            </Box>
          </Box>
        </DialogTitle>

        <IconButton
          onClick={() => setShowModal(false)}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>

        <DialogContent style={{ background: '#dce7f1' }}>
          <CompaniesAutoComplete onSelect={setSelectedCompany} />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            <Button
              variant="contained"
              color="success"
              onClick={() => onPromote()}
              disabled={!selectedCompany || isLoading}
            >
              {isLoading ? 'Loading...' : 'Proceed to Payment'}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    );
  };

  return (
    <div>
      {renderModal()}

      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 1,
          textAlign: 'left',
        }}
      >
        <mark style={{ padding: '3px' }}>Featured companies</mark>
        {slotInfo.isAvailable ? (
          <Button variant="text" sx={{ padding: '0px 8px' }} color="success" onClick={() => setShowModal(true)}>
            Promote a company
          </Button>
        ) : (
          <Chip
            sx={{
              maxWidth: { xs: '100%', sm: 'none' },
              height: 'auto',
              py: 0.5,
              '& .MuiChip-label': { whiteSpace: 'normal' },
            }}
            label={`The nearest available slot is on ${moment(slotInfo.nearestAvailableDate).format(
              'MMMM Do YYYY',
            )}`}
          />
        )}
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, minmax(0, 1fr))',
            md: 'repeat(3, minmax(0, 1fr))',
          },
          gap: { xs: 1.5, sm: 2 },
          alignItems: 'stretch',
          mt: { xs: 0.5, sm: 1 },
        }}
      >
        {companies.map((company) => {
          return (
            <Paper
              key={company._id}
              sx={{
                padding: '12px',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                boxSizing: 'border-box',
              }}
              elevation={3}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  flex: 1,
                  gap: 1.5,
                  textAlign: 'center',
                }}
              >
                {company.logo && (
                  <Box
                    sx={{
                      width: '100%',
                      textAlign: 'center',
                      minHeight: 56,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Box
                      component="img"
                      sx={{
                        maxWidth: '100%',
                        maxHeight: '56px',
                        height: 'auto',
                        width: 'auto',
                        verticalAlign: 'middle',
                        imageRendering: '-webkit-optimize-contrast',
                      }}
                      src={company.logo}
                      alt={company.name}
                    />
                  </Box>
                )}

                <Box sx={{ flex: 1, width: '100%' }}>
                  <Box component="span" sx={{ fontWeight: 600 }}>
                    <a
                      style={{ color: 'inherit' }}
                      href={`/companies/${company.companySlug}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      All Jobs
                    </a>
                  </Box>

                  <Box sx={{ marginTop: '8px', fontSize: '14px', lineHeight: 1.4 }}>
                    <a
                      style={{ margin: '0 4px' }}
                      href={company.jobBoardUrl || `/companies/${company.companySlug}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {company.name}
                    </a>
                    <span style={{ margin: '0 4px', opacity: 0.5 }}>|</span>
                    <span style={{ display: 'inline-block' }}>
                      {company.city ? (
                        <>{company.city ? company.city + ', ' + company.state : ''}</>
                      ) : company.location ? (
                        <>{company.location}</>
                      ) : null}
                    </span>
                  </Box>
                </Box>

                <Button
                  href={company.jobBoardUrl || `/companies/${company.companySlug}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  component="a"
                  variant="contained"
                  color="primary"
                  size="small"
                  fullWidth
                  sx={{ mt: 'auto', maxWidth: '100%' }}
                >
                  Learn more
                </Button>
              </Box>
            </Paper>
          );
        })}
      </Box>
    </div>
  );
};

export default Featured;
